import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import type { SiteBundleDto } from '@portfolio/shared';
import { ThemeToggle } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { Logo } from './Brand';
import { usePrefetchSite, useSite } from './hooks';
import { localizePath, stripLocale, useDocumentLocale, useLocale, type Dictionary } from './i18n';
import { NavToneProvider, useNavToneValue } from './navTone';
import { SmartLink } from './SmartLink';
import { NewsletterSignup } from './NewsletterSignup';
import { SocialIcon, platformName } from './SocialIcon';

/** Scrolls to #hash targets after navigation, waiting for async sections to render. */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  const lastPath = useRef(pathname);
  useEffect(() => {
    const pathChanged = lastPath.current !== pathname;
    lastPath.current = pathname;
    if (!hash) {
      // Only a new page starts at the top — clearing the hash must not jump the page.
      if (pathChanged) window.scrollTo({ top: 0 });
      return;
    }
    let tries = 0;
    const id = decodeURIComponent(hash.slice(1));
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 40) {
        clearInterval(timer);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
    return () => clearInterval(timer);
  }, [pathname, hash]);
  return null;
}

function navItems(s: SiteBundleDto['settings'] | undefined, t: Dictionary) {
  return [
    { href: '#about', label: s?.navAboutLabel ?? t.nav.about },
    { href: '#work', label: s?.navWorkLabel ?? t.nav.work },
    { href: '#services', label: s?.navServicesLabel ?? t.nav.services },
    { href: '#insights', label: s?.navInsightsLabel ?? t.nav.insights },
  ];
}

/** "العربية" / "English": the same page in the other language (hash and query kept). */
function LanguageSwitch({ className }: { className?: string }) {
  const { locale, t } = useLocale();
  const { pathname, search, hash } = useLocation();
  const prefetch = usePrefetchSite();
  const other = locale === 'en' ? 'ar' : 'en';
  const path = localizePath(stripLocale(pathname), other);
  return (
    <Link
      to={`${path}${search}${hash}`}
      lang={other}
      hrefLang={other}
      aria-label={t.switchToLabel}
      title={t.switchToLabel}
      onMouseEnter={() => prefetch(other)}
      onFocus={() => prefetch(other)}
      onTouchStart={() => prefetch(other)}
      className={className}
    >
      {t.switchTo}
    </Link>
  );
}

function Navbar() {
  const { data } = useSite();
  const { t, to } = useLocale();
  const s = data?.settings;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  // Light-on-dark only while the bar is transparent over a dark hero.
  const onDark = useNavToneValue() === 'dark' && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname, hash]);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>('a')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  const items = navItems(s, t);
  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300',
        scrolled || open
          ? 'bg-canvas/85 shadow-[0_1px_0_rgb(0_0_0/0.06)] backdrop-blur-md dark:shadow-[0_1px_0_rgb(255_255_255/0.06)]'
          : 'bg-transparent',
      )}
    >
      <nav aria-label={t.mainNav} className="container-site flex h-16 items-center gap-6 md:h-20">
        <Link to={to('/')} className="shrink-0" aria-label={t.home}>
          <Logo label={s?.ownerName ?? t.home} />
        </Link>
        <ul className="ms-6 hidden items-center lg:flex">
          {items.map((item, i) => (
            <li key={item.href} className="flex items-center">
              {i > 0 && (
                <span aria-hidden className={cn('h-4 w-px', onDark ? 'bg-white/30' : 'bg-line')} />
              )}
              <SmartLink
                href={item.href}
                className={cn(
                  'px-5 text-ui transition-colors',
                  onDark ? 'text-white/80 hover:text-white' : 'text-graphite hover:text-ink',
                )}
              >
                {item.label}
              </SmartLink>
            </li>
          ))}
        </ul>
        <div className="ms-auto flex items-center gap-2">
          <ThemeToggle
            label={t.themeLabel}
            className={cn(
              'size-10 transition-colors',
              onDark
                ? 'text-white/90 hover:bg-white/10 hover:text-white'
                : 'text-muted hover:bg-surface hover:text-ink',
            )}
          />
          <LanguageSwitch
            className={cn(
              'inline-flex h-10 items-center rounded-lg px-3 text-ui transition-colors',
              onDark ? 'text-white/80 hover:text-white' : 'text-graphite hover:text-ink',
            )}
          />
          <SmartLink
            href={s?.navCtaUrl ?? '#contact'}
            className={cn(
              'ms-2 hidden h-10 items-center rounded-lg px-4 text-ui font-semibold transition-colors sm:inline-flex',
              onDark
                ? 'bg-primary text-white hover:bg-primary-hover'
                : 'bg-ink text-canvas hover:bg-primary',
            )}
          >
            {s?.navCtaLabel ?? t.bookCall}
          </SmartLink>
          <button
            ref={toggleRef}
            type="button"
            className={cn(
              'inline-flex size-10 items-center justify-center rounded-lg lg:hidden',
              onDark ? 'text-white hover:bg-white/10' : 'text-ink hover:bg-surface',
            )}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? t.closeMenu : t.openMenu}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            ref={panelRef}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="h-[calc(100dvh-64px)] overflow-y-auto border-t border-line bg-canvas lg:hidden"
          >
            <ul className="container-site flex flex-col py-6">
              {items.map((item) => (
                <li key={item.href} className="border-b border-line">
                  <SmartLink
                    href={item.href}
                    className="flex items-center justify-between py-5 text-title font-light text-ink"
                  >
                    {item.label}
                    <ArrowUpRight className="size-5 text-silver rtl:-scale-x-100" />
                  </SmartLink>
                </li>
              ))}
            </ul>
            <div className="container-site pb-10">
              <SmartLink
                href={s?.navCtaUrl ?? '#contact'}
                className="flex h-12 items-center justify-center rounded-xl bg-ink text-ui font-semibold text-canvas"
              >
                {s?.navCtaLabel ?? t.bookCall}
              </SmartLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  const { data } = useSite();
  const { t } = useLocale();
  const s = data?.settings;
  const name = s?.ownerName ?? '';
  const copyright =
    s?.footerText ??
    `© ${new Date().getFullYear()} ${s?.ownerTagline ?? name}
/ ${name}`;
  const items = navItems(s, t);
  return (
    <footer className="bg-night text-white">
      {s?.newsletterEnabled && (
        <div className="container-site pt-12 pb-12 md:pt-[72px] md:pb-[72px]">
          <NewsletterSignup heading={s.newsletterHeading} intro={s.newsletterIntro} />
        </div>
      )}
      {/* Both dividers run the full extent, as designed: this one edge to edge, the vertical one
          from here to the bottom of the page (it is the start border of the right-hand cells). */}
      <div aria-hidden className="h-px bg-graphite dark:bg-white/15" />
      <div className="container-site grid gap-y-12 pt-12 pb-16 md:grid-cols-[minmax(0,1fr)_auto] md:gap-x-10 md:gap-y-0 md:py-0">
        {/* on mobile the roles sit beside the name, top-aligned; from md they stack beneath it */}
        <div className="flex items-start justify-between gap-6 md:block md:pt-[66px] md:pb-12">
          <div className="shrink-0">
            <p className="w-min text-h2 leading-none font-bold">{name}</p>
            {s?.ownerTagline && (
              <p className="mt-3 text-lead font-light text-graphite">{s.ownerTagline}</p>
            )}
          </div>
          {s?.roles && (
            <p className="min-w-0 text-ui font-light whitespace-pre-line md:mt-[42px] md:text-lead">
              {s.roles}
            </p>
          )}
        </div>

        <nav
          aria-label={t.footerNav}
          className="md:min-w-[319px] md:border-s md:border-graphite md:ps-[90px] md:pt-[66px] md:pb-12 dark:md:border-white/15"
        >
          <ul className="space-y-1 md:space-y-[13px]">
            {items.map((item) => (
              <li key={item.href}>
                <SmartLink
                  href={item.href}
                  className="text-title font-medium transition-colors hover:text-primary"
                >
                  {t.shortNavLabel(item.label)}
                </SmartLink>
              </li>
            ))}
          </ul>
        </nav>

        {!!data?.socials.length && (
          <ul className="flex items-center md:-mt-[2px] md:self-start md:pb-[114px]">
            {/* the footer lists the profiles in reverse order (Instagram first), as designed */}
            {[...data.socials].reverse().map((l, i) => (
              <li key={l.id} className="flex items-center">
                {i > 0 && (
                  <span aria-hidden className="mx-4 h-9 w-px bg-graphite dark:bg-white/15" />
                )}
                <a
                  href={l.url}
                  target={l.url.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="inline-flex items-center py-1.5 transition-colors hover:text-primary"
                  aria-label={`${platformName[l.platform]}: ${l.label}`}
                >
                  <SocialIcon platform={l.platform} className="size-[22px] shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        )}
        <p className="text-lead font-semibold whitespace-pre-line text-graphite md:col-start-2 md:border-s md:border-graphite md:ps-[91px] md:pb-[114px] dark:md:border-white/15">
          {copyright}
        </p>
      </div>
    </footer>
  );
}

export function PublicLayout() {
  useDocumentLocale();
  const { t } = useLocale();
  return (
    <NavToneProvider>
      <div className="flex min-h-dvh flex-col overflow-x-clip">
        <a
          href="#main"
          className="sr-only z-[60] rounded-md bg-ink px-4 py-2 text-canvas focus:not-sr-only focus:fixed focus:start-3 focus:top-3"
        >
          {t.skipToContent}
        </a>
        <ScrollManager />
        <Navbar />
        <main id="main" className="flex-1">
          <Outlet />
        </main>
        <Footer />
      </div>
    </NavToneProvider>
  );
}
